import {
  TROPHY_ANIMALS,
  PREMIUM_GUNS,
  SIGNATURE_BULLETS,
} from "../data/content";
import useReveal from "../hooks/useReveal";

/**
 * Section 2 — "Hunt premium animals with unique weapons"
 * Wildlife → Arsenal → Ammunition showcase.
 */
export default function HuntAndArsenal() {
  const headRef = useReveal();

  return (
    <section
      id="hunt"
      data-testid="hunt-section"
      className="relative bg-[hsl(var(--background))] overflow-hidden"
    >
      {/* Ambient backdrop accents */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[hsl(var(--primary))]/8 blur-[120px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-1/3 -right-32 h-[420px] w-[420px] rounded-full bg-[#22d3ee]/6 blur-[120px]"
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-28 lg:py-36">
        {/* Heading */}
        <div ref={headRef} className="reveal max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            <span
              className="text-[10px] uppercase tracking-[0.28em] text-white/55 font-body"
              data-testid="hunt-eyebrow"
            >
              Featured Wildlife
            </span>
          </span>
          <h2
            data-testid="hunt-title"
            className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl text-white text-balance leading-[1.05]"
          >
            Hunt premium animals
            <br />
            <span className="text-[hsl(var(--primary))]">with unique weapons.</span>
          </h2>
          <p
            className="mt-6 max-w-2xl text-base sm:text-lg text-white/65 font-body leading-relaxed"
            data-testid="hunt-subtitle"
          >
            From the cougar of the high ridges to the white rhino of the bush —
            bring down legendary trophies using a hand-finished arsenal you
            won’t see anywhere else.
          </p>
        </div>

        {/* Animals row */}
        <div className="mt-16 lg:mt-20">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[hsl(var(--primary))]/60" />
            <h3 className="text-sm uppercase tracking-[0.28em] text-white/80 font-body font-medium">
              Trophy Wildlife
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
            {TROPHY_ANIMALS.map((a, i) => (
              <AnimalCard key={a.key} a={a} index={i} />
            ))}
          </div>
        </div>

        {/* Guns row */}
        <div className="mt-20 lg:mt-24">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[hsl(var(--accent))]/70" />
            <h3 className="text-sm uppercase tracking-[0.28em] text-white/80 font-body font-medium">
              Premium Arsenal
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {PREMIUM_GUNS.map((g, i) => (
              <GunCard key={g.key} g={g} index={i} />
            ))}
          </div>
        </div>

        {/* Bullets row */}
        <div className="mt-20 lg:mt-24" id="bullets">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#34d399]/70" />
            <h3 className="text-sm uppercase tracking-[0.28em] text-white/80 font-body font-medium">
              Signature Ammunition
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-6">
            {SIGNATURE_BULLETS.map((b, i) => (
              <BulletCard key={b.key} b={b} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AnimalCard({ a, index }) {
  const ref = useReveal();
  return (
    <article
      ref={ref}
      data-testid={`trophy-animal-${a.key}`}
      style={{ transitionDelay: `${index * 100}ms` }}
      className="reveal group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#15170f] to-[#0b0d08] aspect-[4/5] transition-all duration-500 hover:-translate-y-1 hover:border-[hsl(var(--primary))]/40 hover:shadow-2xl hover:shadow-[hsl(var(--primary))]/10"
    >
      {/* Spotlight gradient */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(217,119,54,0.18)_0%,_transparent_55%)] opacity-0 group-hover:opacity-100 transition-opacity duration-700"
      />

      {/* Animal art — preserves alpha (cougar shifted to show face) */}
      <img
        src={a.image}
        alt={a.name}
        loading="lazy"
        className={`absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.05] ${
          a.key === "cougar" ? "object-left" : "object-center"
        }`}
      />

      {/* Bottom gradient + label */}
      <div className="absolute inset-x-0 bottom-0 z-10 p-6 lg:p-7 bg-gradient-to-t from-[#0b0d08] via-[#0b0d08]/70 to-transparent">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 font-body">
          {a.biome}
        </div>
        <h4
          className="font-display text-2xl lg:text-3xl text-white mt-1.5"
          data-testid={`trophy-animal-name-${a.key}`}
        >
          {a.name}
        </h4>
      </div>
    </article>
  );
}

function GunCard({ g, index }) {
  const ref = useReveal();
  return (
    <article
      ref={ref}
      data-testid={`premium-gun-${g.key}`}
      style={{ transitionDelay: `${index * 120}ms` }}
      className="reveal group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#15170f] via-[#0c0f0a] to-[#0a0d08] p-6 lg:p-7 transition-all duration-500 hover:-translate-y-1"
    >
      {/* Rarity glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          background: `radial-gradient(circle at 70% 30%, ${g.rarityHex}30, transparent 60%)`,
        }}
      />

      <div className="relative flex flex-col h-full">
        {/* Gun art */}
        <div className="relative">
          <div
            aria-hidden="true"
            className="absolute inset-0 rounded-xl"
            style={{
              background: `radial-gradient(ellipse at center, ${g.rarityHex}1c 0%, transparent 65%)`,
            }}
          />
          <img
            src={g.image}
            alt={g.name}
            loading="lazy"
            className="relative h-40 lg:h-48 w-full object-contain transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06] group-hover:-rotate-1 drop-shadow-[0_20px_40px_rgba(0,0,0,0.55)]"
          />
        </div>

        <div className="mt-6">
          <span
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.28em] font-body font-semibold"
            style={{
              color: g.rarityHex,
              background: `${g.rarityHex}14`,
              border: `1px solid ${g.rarityHex}40`,
            }}
            data-testid={`premium-gun-rarity-${g.key}`}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: g.rarityHex }}
            />
            {g.rarity}
          </span>
          <h4
            className="font-display text-xl lg:text-2xl text-white mt-3"
            data-testid={`premium-gun-name-${g.key}`}
          >
            {g.name}
          </h4>
          <p className="mt-3 text-sm text-white/65 font-body leading-relaxed">
            {g.blurb}
          </p>
        </div>
      </div>
    </article>
  );
}

function BulletCard({ b, index }) {
  const ref = useReveal();
  return (
    <article
      ref={ref}
      data-testid={`bullet-${b.key}`}
      style={{ transitionDelay: `${index * 100}ms` }}
      className="reveal group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#15170f] via-[#0c0f0a] to-[#0a0d08] p-6 transition-all duration-500 hover:-translate-y-1"
    >
      {/* Accent glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          background: `radial-gradient(circle at 50% 40%, ${b.accentHex}30, transparent 65%)`,
        }}
      />

      <div className="relative flex items-center gap-5">
        {/* Bullet art */}
        <div className="relative shrink-0">
          <div
            aria-hidden="true"
            className="absolute inset-0 rounded-full blur-2xl"
            style={{ background: `${b.accentHex}30` }}
          />
          <img
            src={b.image}
            alt={b.name}
            loading="lazy"
            className="relative h-24 w-24 lg:h-28 lg:w-28 object-contain drop-shadow-[0_12px_28px_rgba(0,0,0,0.6)] transition-transform duration-[1200ms] group-hover:scale-110 group-hover:rotate-3"
          />
        </div>

        {/* Text */}
        <div className="min-w-0">
          <span
            className="inline-flex items-center gap-2 rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-[0.28em] font-body font-semibold"
            style={{
              color: b.accentHex,
              background: `${b.accentHex}14`,
              border: `1px solid ${b.accentHex}40`,
            }}
          >
            {b.tag}
          </span>
          <h4
            className="font-display text-xl lg:text-2xl text-white mt-2"
            data-testid={`bullet-name-${b.key}`}
          >
            {b.name}
          </h4>
          <p className="mt-1.5 text-xs sm:text-sm text-white/65 font-body leading-relaxed">
            {b.blurb}
          </p>
        </div>
      </div>
    </article>
  );
}
